/** Copy of the graph lights view, in one place. */
import { CAUSE_STATUS, EDGE_MODES, ROLES, STAGES, THEME_MODES, TONES } from "./lightsConstants";

const pct = (v) => `${Math.round(v)} %`;

export const LIGHTS_TEXT = Object.freeze({
  title: "Graph view",
  close: "Close graph view",
  recentre: "Fit",
  recentreHint: "Fit the lit causes (0 or double-click)",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  loading: "Loading the category graph…",
  empty: "This category has no graph data yet.",
  hint: "Scroll to zoom · drag to pan · click a node or an edge for details · [ and ] step through lit nodes · Esc clears",
  legendLabel: "Legend",
  liveLabel: "Lit causes",
  controlsLabel: "View options",
  edges: "Edges",
  edgeModes: { [EDGE_MODES.ALL]: "All", [EDGE_MODES.TRAVERSED]: "Traversed", [EDGE_MODES.NONE]: "Off" },
  toggles: { labels: "Labels", facts: "Facts", embers: "Ruled out" },
  themes: { [THEME_MODES.NIGHT]: "Night", [THEME_MODES.DAY]: "Day" },
  themeHint: "Switch between the night and day palette",
  search: "Find a node",
  searchPlaceholder: "Search causes, symptoms, facts…",
  noMatch: "No node matches.",
  status: "Diagnosis",
  topCauses: "Top causes",
  ruledOut: "Ruled out",
  lines: "Lines of diagnosis",
  noCauses: "Lights come on with the first symptoms.",
  back: "Back to the list",
  details: "Details",
  roles: { [ROLES.CAUSE]: "Possible cause", [ROLES.SYMPTOM]: "Symptom", [ROLES.FACT]: "Fact" },
  causeStatus: {
    [CAUSE_STATUS.LEADING]: "Leading", [CAUSE_STATUS.CONFIRMED]: "Confirmed", [CAUSE_STATUS.EVALUATING]: "Being checked",
    [CAUSE_STATUS.RULED_OUT]: "Ruled out", [CAUSE_STATUS.FAILED]: "Fix failed", [CAUSE_STATUS.LIVE]: "Live",
  },
  observedTone: { [TONES.LIT]: "observed", [TONES.DENIED]: "answered no", [TONES.FADED]: "no live cause left" },
  observedValue: { yes: "Observed: yes", no: "Observed: no", unknown: "Observed: unknown" },
  observedSource: (s) => `from ${s}`,
  notObserved: "Not observed in this chat.",
  legend: [
    { role: ROLES.CAUSE, tone: TONES.DIM, label: "Cause in this category" },
    { role: ROLES.CAUSE, tone: TONES.LIT, label: "Candidate cause (brighter = likelier)" },
    { role: ROLES.CAUSE, tone: TONES.LEADING, label: "Leading cause" },
    { role: ROLES.CAUSE, tone: TONES.EMBER, label: "Ruled out" },
    { role: ROLES.SYMPTOM, tone: TONES.LIT, label: "Symptom seen" },
    { role: ROLES.FACT, tone: TONES.LIT, label: "Fact seen" },
    { role: ROLES.FACT, tone: TONES.DENIED, label: "Answered no" },
  ],
  legendEdges: [
    { kind: "faint", label: "Relation in the category" },
    { kind: "lit", label: "Traversed: evidence seen → cause" },
    { kind: "lead", label: "Path to the leading cause" },
    { kind: "rules_out", label: "Ruled out by this fact" },
  ],
  edge: {
    supports: "supports",
    rulesOut: "rules out",
    kind: { supports: "Supports", rules_out: "Rules out" },
    traversed: "Traversed in this chat",
    notTraversed: "Not traversed yet",
    relation: "Relation",
    from: "From",
    to: "To",
    whyMatters: "Why it matters",
    seen: (seen, wins) => (seen ? `Seen together in ${seen} conversation${seen === 1 ? "" : "s"}, pointed to this cause in ${wins}.` : "Not seen in past conversations yet."),
    ruledHere: (why) => `This answer ruled the cause out${why ? `: ${why}` : "."}`,
    clue: "Counted as a clue for this cause.",
    moved: (d) => `The last answer moved the cause by ${d > 0 ? "+" : ""}${d} pts.`,
    noObservation: "The fact has not been observed, so this relation carries no weight yet.",
    relations: "Relations in the graph",
    openEdge: "Show this relation",
  },
  detail: {
    confidence: (p) => `Confidence ${pct(p)}`,
    ruledHere: "Ruled out in this chat",
    notInChat: "Not among the possible causes of this chat yet.",
    howReached: (p) => `How ${pct(p)} was reached`,
    noChain: "Detail not available for this cause.",
    inChat: "In this chat",
    pointsTo: "Points to it",
    against: "Argues against it",
    reason: "Reason",
    checks: "How it is confirmed",
    noChecks: "No checks are stored for this cause.",
    checkWord: { done: "Confirmed", failed: "Not confirmed", current: "Checking now", pending: "Not checked yet" },
    checkMark: { done: "✓", failed: "✗", current: "▶", pending: "○" },
    fix: "Fix",
    escalation: "Escalation",
    rootCause: "Root cause",
    tried: (t, w) => `Tried ${t} time${t === 1 ? "" : "s"}, worked ${w}.`,
    supports: "Evidence that points here",
    rulesOut: "What rules it out",
    track: "Track record",
    trackLine: (cases, trust) => `${cases} confirmed conversation${cases === 1 ? "" : "s"}${trust != null ? ` · trust ${trust.toFixed(2)}` : ""}`,
    lastSeen: (d) => `Last seen ${d}`,
    sources: "Sources",
    why: "Why: ",
    loading: "Loading the write-up…",
    unavailable: "Write-up not available.",
    writing: "The summary is still being written…",
    seen: (seen, wins) => (seen ? `Seen in ${seen} conversation${seen === 1 ? "" : "s"}, pointed here in ${wins}` : "Not seen in past conversations yet"),
    fromGuide: "From the guide",
    supportsCauses: "Supports",
    rulesOutCauses: "Rules out",
    noRelations: "No relation stored for this node.",
    line: "Line",
  },
});

/** One-line caption of where the diagnosis is, for the header. */
export function stageCaption(stage, summary) {
  const lead = summary.leading[0];
  switch (stage) {
    case STAGES.CANDIDATES: return `${summary.lit} of ${summary.causes} causes lit`;
    case STAGES.NARROWING: return `${summary.lit} causes still lit · ${summary.ruledOut} ruled out`;
    case STAGES.LIKELY: return lead ? `Homing in on ${lead.label} (${pct(lead.pct)})` : "Homing in on the likely cause";
    case STAGES.ENDED: return "Diagnosis ended · final picture";
    default: return `${summary.causes} causes · lights come on with the first symptoms`;
  }
}

export const STAGE_WORD = Object.freeze({
  [STAGES.NONE]: "No category", [STAGES.CATEGORY]: "Category loaded", [STAGES.CANDIDATES]: "Candidates lit",
  [STAGES.NARROWING]: "Narrowing down", [STAGES.LIKELY]: "Likely cause", [STAGES.ENDED]: "Ended",
});

/** The canvas aria-label, rewritten each turn. */
export function canvasLabel(categoryLabel, summary) {
  const lead = summary.leading[0];
  const tail = lead ? `, leading: ${lead.label} ${pct(lead.pct)}` : "";
  return `${categoryLabel}: ${summary.causes} causes, ${summary.lit} candidates lit${tail}`;
}

/** Tooltip second line for one node. */
export function nodeDetail(node, clusterLabel) {
  const parts = [LIGHTS_TEXT.roles[node.role]];
  if (node.role === ROLES.CAUSE) {
    if (node.pct != null && node.tone !== TONES.EMBER) parts.push(pct(node.pct));
    if (node.status && LIGHTS_TEXT.causeStatus[node.status]) parts.push(LIGHTS_TEXT.causeStatus[node.status].toLowerCase());
    else if (node.tone === TONES.EMBER) parts.push(LIGHTS_TEXT.causeStatus[CAUSE_STATUS.RULED_OUT].toLowerCase());
  } else if (LIGHTS_TEXT.observedTone[node.tone]) {
    parts.push(LIGHTS_TEXT.observedTone[node.tone]);
  }
  if (clusterLabel) parts.push(clusterLabel);
  return parts.join(" · ");
}

/** Tooltip lines for a hovered edge: "<fact> supports <cause>", kind / traversed, the observed answer. */
export function edgeTooltip(edge, lights) {
  const a = lights.nodes.get(edge.src);
  const b = lights.nodes.get(edge.dst);
  const verb = edge.kind === "rules_out" ? LIGHTS_TEXT.edge.rulesOut : LIGHTS_TEXT.edge.supports;
  const parts = [edge.traversed ? LIGHTS_TEXT.edge.traversed : LIGHTS_TEXT.edge.notTraversed];
  if (a?.observed) parts.push(LIGHTS_TEXT.observedValue[a.observed.value] || LIGHTS_TEXT.observedValue.unknown);
  if (b?.pct != null && b.tone !== TONES.EMBER) parts.push(pct(b.pct));
  return { title: `${a?.label || edge.src} ${verb} ${b?.label || edge.dst}`, detail: parts.join(" · ") };
}

export { pct as formatPercent };
